import { isMobileApp } from '@/utils/general/device-info';
import * as ScreenOrientation from 'expo-screen-orientation';
import { Dimensions } from 'react-native';
// TODO: P1: Add all orientations
// TODO: P3: Add all hooks
// TODO: P3: Add all relevant functions and getAutoRotateState

type MB_ScreenOrientationInfo = {
  /**
   * The current orientation of the device.
   */
  orientation: MB_ORIENTATION;

  /**
   * Boolean flag indicating wether screen is in portrait mode or not.
   */
  isPortrait: boolean;

  /**
   * The [vertical size class](https://developer.apple.com/library/archive/featuredarticles/ViewControllerPGforiPhoneOS/TheAdaptiveModel.html)
   * of the device.
   * @platform ios
   */
  verticalSizeClass?: ScreenOrientation.SizeClassIOS;
  /**
   * The [horizontal size class](https://developer.apple.com/library/archive/featuredarticles/ViewControllerPGforiPhoneOS/TheAdaptiveModel.html)
   * of the device.
   * @platform ios
   */
  horizontalSizeClass?: ScreenOrientation.SizeClassIOS;
};

enum MB_ORIENTATION {
  /**
   * An unknown screen orientation. For example, the device is flat, perhaps on a table.
   */
  UNKNOWN = 'UNKNOWN',
  /**
   * Right-side up portrait interface orientation.
   */
  PORTRAIT_UP = 'PORTRAIT_UP',
  /**
   * Upside down portrait interface orientation.
   */
  PORTRAIT_DOWN = 'PORTRAIT_DOWN',
  /**
   * Left landscape interface orientation.
   */
  LANDSCAPE_LEFT = 'LANDSCAPE_LEFT',
  /**
   * Right landscape interface orientation.
   */
  LANDSCAPE_RIGHT = 'LANDSCAPE_RIGHT',
}

enum MB_ORIENTATION_LOCK {
  /**
   * The default orientation. On iOS, this will allow all orientations except `Orientation.PORTRAIT_DOWN`.
   * On Android, this lets the system decide the best orientation.
   */
  DEFAULT = 'DEFAULT',
  /**
   * All four possible orientations
   */
  ALL = 'ALL',
  /**
   * Any portrait orientation.
   */
  PORTRAIT = 'PORTRAIT',
  /**
   * Right-side up portrait only.
   */
  PORTRAIT_UP = 'PORTRAIT_UP',
  /**
   * Upside down portrait only.
   */
  PORTRAIT_DOWN = 'PORTRAIT_DOWN',
  /**
   * Any landscape orientation.
   */
  LANDSCAPE = 'LANDSCAPE',
  /**
   * Left landscape only.
   */
  LANDSCAPE_LEFT = 'LANDSCAPE_LEFT',
  /**
   * Right landscape only.
   */
  LANDSCAPE_RIGHT = 'LANDSCAPE_RIGHT',
  /**
   * A platform specific orientation. This is not a valid policy that can be applied in [`lockAsync`](#screenorientationlockasyncorientationlock).
   */
  OTHER = 'OTHER',
  /**
   * An unknown screen orientation lock. This is not a valid policy that can be applied in [`lockAsync`](#screenorientationlockasyncorientationlock).
   */
  UNKNOWN = 'UNKNOWN',
}

/**
 * Portrait/landscape decision for layout code. expo-screen-orientation can report
 * UNKNOWN (its iOS cache starts as .unknown and is only refreshed by a real interface
 * rotation; some launch paths never trigger one). UNKNOWN used to be treated as "not
 * portrait", which rendered the landscape game layout on an upright phone with the answer
 * grid cut off and unscrollable. Fall back to the window's actual shape instead.
 */
function isPortraitOrientation(orientation: ScreenOrientation.Orientation): boolean {
  switch (orientation) {
    case ScreenOrientation.Orientation.PORTRAIT_UP:
    case ScreenOrientation.Orientation.PORTRAIT_DOWN:
      return true;
    case ScreenOrientation.Orientation.LANDSCAPE_LEFT:
    case ScreenOrientation.Orientation.LANDSCAPE_RIGHT:
      return false;
    default: {
      const { width, height } = Dimensions.get('window');
      return height >= width;
    }
  }
}

function orientationToMBOrientation(orientation: ScreenOrientation.Orientation): MB_ORIENTATION {
  switch (orientation) {
    case ScreenOrientation.Orientation.UNKNOWN:
      return MB_ORIENTATION.UNKNOWN;
    case ScreenOrientation.Orientation.PORTRAIT_UP:
      return MB_ORIENTATION.PORTRAIT_UP;
    case ScreenOrientation.Orientation.PORTRAIT_DOWN:
      return MB_ORIENTATION.PORTRAIT_DOWN;
    case ScreenOrientation.Orientation.LANDSCAPE_LEFT:
      return MB_ORIENTATION.LANDSCAPE_LEFT;
    case ScreenOrientation.Orientation.LANDSCAPE_RIGHT:
      return MB_ORIENTATION.LANDSCAPE_RIGHT;
    default:
      throw new Error('Invalid orientation');
  }
}

function orientationLockToMBOrientationLock(orientationLock: ScreenOrientation.OrientationLock): MB_ORIENTATION_LOCK {
  switch (orientationLock) {
    case ScreenOrientation.OrientationLock.DEFAULT:
      return MB_ORIENTATION_LOCK.DEFAULT;
    case ScreenOrientation.OrientationLock.ALL:
      return MB_ORIENTATION_LOCK.ALL;
    case ScreenOrientation.OrientationLock.PORTRAIT:
      return MB_ORIENTATION_LOCK.PORTRAIT;
    case ScreenOrientation.OrientationLock.PORTRAIT_UP:
      return MB_ORIENTATION_LOCK.PORTRAIT_UP;
    case ScreenOrientation.OrientationLock.PORTRAIT_DOWN:
      return MB_ORIENTATION_LOCK.PORTRAIT_DOWN;
    case ScreenOrientation.OrientationLock.LANDSCAPE:
      return MB_ORIENTATION_LOCK.LANDSCAPE;
    case ScreenOrientation.OrientationLock.LANDSCAPE_LEFT:
      return MB_ORIENTATION_LOCK.LANDSCAPE_LEFT;
    case ScreenOrientation.OrientationLock.LANDSCAPE_RIGHT:
      return MB_ORIENTATION_LOCK.LANDSCAPE_RIGHT;
    case ScreenOrientation.OrientationLock.OTHER:
      return MB_ORIENTATION_LOCK.OTHER;
    case ScreenOrientation.OrientationLock.UNKNOWN:
      return MB_ORIENTATION_LOCK.UNKNOWN;
    default:
      throw new Error('Invalid orientation lock');
  }
}

function mbOrientationLockToOrientationLock(orientationLock: MB_ORIENTATION_LOCK): ScreenOrientation.OrientationLock {
  switch (orientationLock) {
    case MB_ORIENTATION_LOCK.DEFAULT:
      return ScreenOrientation.OrientationLock.DEFAULT;
    case MB_ORIENTATION_LOCK.ALL:
      return ScreenOrientation.OrientationLock.ALL;
    case MB_ORIENTATION_LOCK.PORTRAIT:
      return ScreenOrientation.OrientationLock.PORTRAIT;
    case MB_ORIENTATION_LOCK.PORTRAIT_UP:
      return ScreenOrientation.OrientationLock.PORTRAIT_UP;
    case MB_ORIENTATION_LOCK.PORTRAIT_DOWN:
      return ScreenOrientation.OrientationLock.PORTRAIT_DOWN;
    case MB_ORIENTATION_LOCK.LANDSCAPE:
      return ScreenOrientation.OrientationLock.LANDSCAPE;
    case MB_ORIENTATION_LOCK.LANDSCAPE_LEFT:
      return ScreenOrientation.OrientationLock.LANDSCAPE_LEFT;
    case MB_ORIENTATION_LOCK.LANDSCAPE_RIGHT:
      return ScreenOrientation.OrientationLock.LANDSCAPE_RIGHT;
    case MB_ORIENTATION_LOCK.OTHER:
      return ScreenOrientation.OrientationLock.OTHER;
    case MB_ORIENTATION_LOCK.UNKNOWN:
      return ScreenOrientation.OrientationLock.UNKNOWN;
    default:
      throw new Error('Invalid orientation lock');
  }
}

let _defaultOrientation: MB_ORIENTATION_LOCK = MB_ORIENTATION_LOCK.DEFAULT;

const MB_orientationLocker = {
  /**
   * Used for storing the original orientation so we can restore it later on if needed.
   * @param defaultOrientation Default orientation of the app
   * @param applyDefaultOrientation if true, the defaultOrientation will also be immediatley applied to the app
   */
  setDefaultOrientation: (defaultOrientation: MB_ORIENTATION_LOCK, applyDefaultOrientation?: boolean) => {
    _defaultOrientation = defaultOrientation;

    if (!applyDefaultOrientation) {
      MB_orientationLocker.setOrientation(_defaultOrientation);
    }
  },
  setOrientation: (newOrientation: MB_ORIENTATION_LOCK) => {
    if (isMobileApp) {
      ScreenOrientation.lockAsync(mbOrientationLockToOrientationLock(newOrientation));
    }
  },
  restoreDefault: () => {
    MB_orientationLocker.setOrientation(_defaultOrientation);
  },

  /**
   * Gets the current orientation of the device
   * @returns MB_Orientation
   */
  getOrientationAync: async (): Promise<{ orientation: MB_ORIENTATION; isPortrait: boolean }> => {
    const orientation = await ScreenOrientation.getOrientationAsync();
    return {
      orientation: orientationToMBOrientation(orientation),
      isPortrait: isPortraitOrientation(orientation),
    };
  },

  // TODO: Do we actually need the remove orientation listener ? We can just call remove on whatever this function returns.
  addOrientationChangeListener: (callback: (arg: { orientationLock: MB_ORIENTATION_LOCK; orientationInfo: MB_ScreenOrientationInfo }) => void): ScreenOrientation.Subscription => {
    if (!isMobileApp) {
      return {
        remove: () => {},
      };
    }

    return ScreenOrientation.addOrientationChangeListener(({ orientationLock, orientationInfo }) => {
      callback({
        orientationLock: orientationLockToMBOrientationLock(orientationLock),
        orientationInfo: {
          orientation: orientationToMBOrientation(orientationInfo.orientation),
          verticalSizeClass: orientationInfo.verticalSizeClass,
          horizontalSizeClass: orientationInfo.horizontalSizeClass,
          isPortrait: isPortraitOrientation(orientationInfo.orientation),
        },
      });
    });
  },
  removeOrientationChangeListener: ScreenOrientation.removeOrientationChangeListener,
};

export { MB_orientationLocker, MB_ORIENTATION_LOCK, MB_ORIENTATION, isPortraitOrientation };
export type { MB_ScreenOrientationInfo };
